<template>
  <full-page-loader :loading="isLoading"/>
  <layout-wrapper class="bg-light-grey">
    <template v-if="hasProduct">
      <bread-crumbs
          :items="breadCrumbsArray"
          class="my-2 px-1 sm:custom-container hidden sm:block"
      />
      <div class="flex md:flex-row flex-col custom-container bg-white mb-5 p-8">
        <!-- Product Gallery -->
        <div class="md:w-3/5 w-full">
          <gallery :key="galleryRefetchKey" :data="data" :images="images"/>
        </div>

        <!-- Magnified image area -->
        <div class="relative">
          <div
              id="zoom-container"
              class="absolute z-10 ltr:ml-[-150px] rtl:mr-[-150px]"
          ></div>
        </div>

        <!-- Product details -->
        <div class="md:w-2/5 w-full md:max-w-[450px]">
          <div class="flex items-center mb-4">
            <nuxt-link :to="$routerUrl(data?.manufacturer_seo_url)">
              <niceone-image
                  :src="data?.manufacturer_image"
                  class="ltr:mr-2 rtl:ml-2 max-w-[150px]"
              />
            </nuxt-link>
            <div>
              <div class="text-success">{{ $t("product.original_100") }}</div>
              <span>{{ $t("product.click_here_for_more_of") }}</span>
              <nuxt-link
                  :to="$routerUrl(data?.manufacturer_seo_url)"
                  class="font-niceone-medium text-blue-purple ltr:ml-1 rtl:mr-1"
              >{{ data?.manufacturer }}
              </nuxt-link>
            </div>
          </div>
          <div class="flex justify-between mb-3">
            <div class="flex items-center">
              <span class="text-color-[#f5c523]">{{ data?.rating }}</span>
              <van-rate
                  v-model="data.rating"
                  allow-half
                  class="px-2"
                  color="#f5c523"
                  readonly
                  size="18"
                  void-color="#eee"
                  void-icon="star"
              />
              <span>{{
                  $t("product.reviews.reviews", {
                    review_total: reviewsData?.review_total
                  })
                }}</span>
            </div>
            <product-features :id="id" :data="data"/>
          </div>

          <h1 class="text-xl mb-4">{{ data?.name }}</h1>
          <product-attributes
              v-if="data?.has_option"
              :product="data"
              :selectedOptionsAttributes="selectedOptionsAttributes"
              class="pb-4"
              @reset="$resetProductAttributes"
          >
            <template #default-option>
              <product-options
                  v-model="selectedOptionsAttributes.defaultOption"
                  :options="data.options"
                  :placeholder="$t('product.reviews.please_select_option')"
                  class="mb-3"
                  @change="galleryRefetchKey++"
              />
            </template>
            <template #second-option>
              <product-options
                  v-model="selectedOptionsAttributes.secondaryOption"
                  :options="data.options"
                  :placeholder="$t('product.reviews.please_select_option')"
                  class="mb-3"
                  @change="galleryRefetchKey++"
              />
            </template>
          </product-attributes>
          <div class="mb-5">
            <span class="flex items-center justify-between">
              <price :increase-font-by="8" :min-height="0" :product="data"
                     :selected-option="selectedOptionsAttributes.defaultOption"/>
              <div class="flex gap-2">
                <points-tag :data="data" :selected-option="selectedOptionsAttributes"/>
                <product-tags-pdp :selected-option="selectedOptionsAttributes.defaultOption" :tag="data?.tag"/>
              </div>
          </span>
            <span v-if="data?.taxText" class="text-color-[#797979] text-[12px]">
              {{ data?.taxText }}
            </span>
            <!-- <marketing-banner class="mt-2"/> -->
          </div>
          <loyalty-tags :data="data" :selected-option="selectedOptionsAttributes"/>
          <infos :items="data?.infos" class="mx-0"/>
          <installment-widgets :data="data"/>
          <template v-if="data?.in_offer">
            <pdp-offers :id="id" :selected-option="selectedOptionsAttributes"/>
          </template>
          <cart-button
              :id="id"
              :data="data"
              :selected-option="selectedOptionsAttributes.defaultOption"
              :selectedOptionsAttributes="selectedOptionsAttributes"
              :source-data="setEventsData(data)"
          />
          <estimated-time class="mt-3 mb-3"/>
          <template
              v-if="data?.related_products && data?.related_products?.length"
          >
            <related-products
                :id="id"
                :data="data"
                :products="data.related_products"
                :selected-option="
                selectedOptionsAttributes.defaultOption
              "
                class="my-5"
            />
          </template>
        </div>
      </div>

      <div class="custom-container bg-white p-4 mb-5">
        <div class="product-details">
          <product-details :product="data" :reviews="reviewsData">
            <template #reviews>
              <product-reviews
                  v-model:page="page"
                  v-model:sort="sort"
                  :is-load-more="hasNextPage"
                  :loading="loading"
                  :product="data"
                  :reviews="reviews"
                  :reviewsData="reviewsData"
              />
            </template>
          </product-details>
        </div>
      </div>
      <!-- Recommended Products  -->
      <div class="custom-container mb-5">
        <product-list-section
            :data="{ productId: data.id, category: parseInt(data.category_id) }"
            :source-data="EVENT_SOURCES.RELATED_PRODUCTS_SECTION"
        >
          <template #title>
            <div class="text-lg font-niceone-medium">
              {{ $t("product.recommended_products") }}
            </div>
          </template>
        </product-list-section>
      </div>
    </template>

    <template v-if="noProductFound">
      <error/>
    </template>
  </layout-wrapper>
</template>

<script setup>
import Price from "@/components/product/Price";
import Infos from "@/components/product/Infos";
import LoyaltyTags from "@/components/product/loyalty/";
import PointsTag from "@/components/product/loyalty/PointsTag";
import EstimatedTime from "@/components/product/EstimatedTime";
import Gallery from "@/components/product/gallery/desktop";
import ProductOptions from "@/components/product/Options";
import RelatedProducts from "@/components/product/RelatedProducts";
import ProductReviews from "@/components/product/reviews/ReviewsList";
import CartButton from "@/components/product/ProductDetailsCartButton";
import ProductListSection from "@/components/product/ProductListSection";
import ProductDetails from "@/components/product/Details";
import BreadCrumbs from "@/components/common/BreadCrumbs";
import ProductFeatures from "@/components/product/ProductFeatures";
import PdpOffers from "@/components/product/offers/index";
import error from "@/components/error";
import InstallmentWidgets from "@/components/product/installment-widgets/index";
import {track} from "@/composables/useTrackEvent";
import ProductJsonld from "@/jsonld/product";
import {TRACKERS, EVENT_SOURCES} from "@/constants/events";
import {useCartStore} from "@/store/cart";
import ProductHead from "@/head/product";
import {useCustomFetch} from "@/composables/useFetch";
import ProductTagsPdp from "@/components/tags/ProductTagsPDP";
import {useSeoUrlRedirection} from "@/composables/useValidLinkRedirection"
import {BLACKLISTED_PRODUCTS_IDS, SCHEMA_POSITIONED_PRODUCTS} from "@/constants";
import {useProductUrlParams} from "@/composables/useProductUrlParams";

const {selectedOptionsAttributes} = storeToRefs(useCartStore());
const {$resetProductAttributes} = useCartStore();
const route = useRoute();

const {id, optionId} = useProductUrlParams()

const preview = computed(() => route.query?.preview)

const sort = ref("desc");
const page = ref(1);
const limit = ref(10);
const isLoadMore = ref(true);

const images = computed(() => {
  const defaultOption = selectedOptionsAttributes.value.defaultOption;
  const defaultImages = data.value?.images || [];

  if (!defaultOption?.image) {
    return [...defaultImages];
  }

  // correct order for images
  return [
    defaultOption.image,
    ...(defaultOption.additional_images || []),
    ...defaultImages,
  ];
});

const galleryRefetchKey = ref(1);

const params = {
  id,
  ...(preview.value && {preview})
};

// Get product details
const {data, loading: isLoading} = await useCustomFetch(
    "?route=rest/product_admin/products",
    params
);

await useSeoUrlRedirection(data.value?.seo_url, optionId)

const breadCrumbsArray = computed(() => {
  const categories = data.value?.category_hierarchy;
  if (categories?.length) {
    return [
      ...categories.map(item => ({
        title: item.name,
        link: item.seo_url
      })),
      {title: data.value?.name}
    ];
  }
  return [{title: data.value?.name}];
});

const hasProduct = computed(() => {
  if (BLACKLISTED_PRODUCTS_IDS.includes(Number(id))) return false
  return !isLoading.value && data.value?.id
})

const noProductFound = computed(() => {
  if (BLACKLISTED_PRODUCTS_IDS.includes(Number(id))) return true
  return !isLoading.value && !data.value?.id
})

// Get product reviews
const {data: reviewsData, loading: loading} = await useCustomFetch(
    "?route=rest/reviews/product_reviews",
    {
      id,
      offset: page,
      limit,
      sort,
    }
);

const reviews = ref([]);
const updateReviews = () => {
  reviews.value = page.value === 1
      ? reviewsData.value?.reviews
      : [...reviews.value, ...(reviewsData.value?.reviews || [])];
};


// Call updateReviews when appropriate (e.g., when `reviewsData` changes)
watch(
    reviewsData,
    () => {
      updateReviews();
    },
    {immediate: true}
);

if (SCHEMA_POSITIONED_PRODUCTS.includes(Number(id))) {
  // Json schema
  useJsonld(
      ProductJsonld({product: data.value, productReviews: reviewsData.value})
  );
}

const {t} = useI18n();
// Head schema
useHead({
  titleTemplate: (name) => t("seo.product_title_template", {name}),
  title: () => data.value?.name,
  ...ProductHead({product: data.value}),
});

if (!SCHEMA_POSITIONED_PRODUCTS.includes(Number(id))) {
  // Json schema
  useJsonld(
      ProductJsonld({product: data.value, productReviews: reviewsData.value})
  );
}

const hasNextPage = computed(() => {
  if (!reviewsData.value?.review_total) return (isLoadMore.value = false);
  return reviews.value?.length < Number(reviewsData.value?.review_total);
});

const selectProductOptionFromUrl = () => {

  if (!optionId) return

  // Select the option based on optionId from the URL
  const selectedOption = data.value?.has_option && data.value?.options?.[0]?.option_value?.find(item => Number(item.product_option_variant_id) === Number(optionId))
  selectedOptionsAttributes.value.defaultOption = selectedOption || null
}

watchEffect(() => {
  if (data.value) {
    track(TRACKERS.PRODUCT_DETAILS_VIEWED, data.value);
    selectProductOptionFromUrl()
  }
})

const setEventsData = (product) => {
  return {...EVENT_SOURCES.PRODUCT_DETAILS_PAGE, source_name: product?.en_name, source_id: product.id, quick_add: false}
}
</script>

<style></style>
